import type { TOC } from '@ember/component/template-only';
import { concat, fn, get, hash } from '@ember/helper';
import { on } from '@ember/modifier';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import { didUpdate } from '@ember/render-modifiers';
import { LinkTo } from '@ember/routing';
import {
  PrismButton,
  PrismCard,
  PrismCheckbox,
  PrismIcon,
  PrismIllustration,
  PrismLoadingBar,
  PrismModal,
  PrismSelectInput,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { pageTitle } from 'ember-page-title';
import { gt } from 'ember-truth-helpers';
import { RI_BREAKDOWNS } from 'frontend-utils/utils/constants/constant-values';
import GeneralCurrencyPicker from 'lumen/components/general/currency-picker';
import GeneralDatepickersDatepickerWithSidebar from 'lumen/components/general/datepickers/datepicker-with-sidebar';
import GeneralFilterBarDivider from 'lumen/components/general/filter-bar/divider';
import GeneralFilterBarFilterBar from 'lumen/components/general/filter-bar/filter-bar';
import GeneralPageHeader from 'lumen/components/general/page/header';
import GeneralPageMain from 'lumen/components/general/page/main';
import HotelAndMarketRevenueInsightChartContainer from 'lumen/components/hotel-and-market/revenue-insight/chart-container';
import HotelAndMarketRevenueInsightChartsChartPace from 'lumen/components/hotel-and-market/revenue-insight/charts/chart-pace';
import HotelAndMarketRevenueInsightChartsChartStayDate from 'lumen/components/hotel-and-market/revenue-insight/charts/chart-stay-date';
import HotelAndMarketRevenueInsightDatepickersTargetPicker from 'lumen/components/hotel-and-market/revenue-insight/datepickers/target-picker';
import HotelAndMarketRevenueInsightFixedKpiHeader from 'lumen/components/hotel-and-market/revenue-insight/fixed-kpi-header';
import HotelAndMarketRevenueInsightHotelBlockHotelBlock from 'lumen/components/hotel-and-market/revenue-insight/hotel-block/hotel-block';
import HotelAndMarketRevenueInsightKpiKpiSection from 'lumen/components/hotel-and-market/revenue-insight/kpi/kpi-section';
import HotelAndMarketRevenueInsightLoadingStateTitle from 'lumen/components/hotel-and-market/revenue-insight/loading-state-title';
import HotelAndMarketRevenueInsightSectionTitle from 'lumen/components/hotel-and-market/revenue-insight/section-title';
import HotelAndMarketRevenueInsightSegmentationSegmentationBlock from 'lumen/components/hotel-and-market/revenue-insight/segmentation/segmentation-block';
import HotelAndMarketRevenueInsightSortAndReorderBlocks from 'lumen/components/hotel-and-market/revenue-insight/sort-and-reorder-blocks';
import type MarketRevenueInsightController from 'lumen/controllers/market/revenue-insight';
import contextualPageTitle from 'lumen/helpers/contextual-page-title';
import { FILTER_BAR_PAGETYPES } from 'lumen/utils/constants/general-constants';
import {
  ALL_SELECTABLE_SEGMENTATION_BLOCKS,
  MAX_DRAGGABLE_DAYS,
} from 'lumen/utils/constants/ri-constant-values';

interface MarketRevenueInsightSignature {
  Args: {
    controller: MarketRevenueInsightController;
    model: unknown;
  };
}

<template>
  {{#if @controller.riSelections.isCancellationsOnlyView}}
    {{pageTitle
      (contextualPageTitle
        (t "revenue_insight.view_selection.cancellations_only")
      )
    }}
  {{else}}
    {{pageTitle
      (contextualPageTitle (t "revenue_insight.view_selection.net_bookings"))
    }}
  {{/if}}
  <GeneralPageHeader
    class="revenue-insight"
    role="tooltip"
    {{on "mouseenter" (fn @controller.setIsFilterBarHovered true)}}
    {{on "mouseleave" (fn @controller.setIsFilterBarHovered false)}}
  >
    <div class="content-wrapper relative flex h-[60px]">
      <div class="mr-200">
        {{! @glint-expect-error: Incorrect type }}
        <GeneralDatepickersDatepickerWithSidebar
          @displayEvents={{false}}
          @maxDate={{@controller.datePickerMaxDate}}
          @maxRange="12 months"
          @minDate={{@controller.datePickerMinDate}}
          @range={{@controller.range}}
          @rangeChanged={{@controller.rangeChanged}}
          @selectSingleDay={{false}}
          @showMonthDropdowns={{true}}
          @sidebarLists={{@controller.datePickerRangeOptions}}
        />
      </div>

      {{! @glint-expect-error: Incorrect type }}
      <HotelAndMarketRevenueInsightDatepickersTargetPicker
        @displayEvents={{false}}
        @maxDate={{@controller.datePickerRefMaxDate}}
        @maxRange="12 months"
        @minDate={{@controller.datePickerRefMinDate}}
        @nrOfDays={{@controller.referencePeriod}}
        @range={{@controller.refRange}}
        @rangeChanged={{@controller.refRangeChanged}}
        @selectedOptionId={{@controller.reference}}
        @setHidePickupLY={{@controller.setHidePickupLY}}
        @sidebarLists={{@controller.refDatePickerRangeOptions}}
      />

      <GeneralFilterBarFilterBar
        @activeFilters={{@controller.activeFilters}}
        @favourites={{@controller.favourites}}
        {{! @glint-expect-error: Incorrect type }}
        @filterBarSegmentationClicked={{@controller.filterBarSegmentationClicked}}
        @filtersToCheckForVisibility={{ALL_SELECTABLE_SEGMENTATION_BLOCKS}}
        @isFavouritesDisabled={{@controller.isFavouritesDisabled}}
        @isHovered={{@controller.isFilterBarHovered}}
        @isLoading={{@controller.showLoadingPlaceholderState}}
        {{! @glint-expect-error: Incorrect type }}
        @market={{@controller.hotelOrMarket}}
        @pageType={{FILTER_BAR_PAGETYPES.REVENUE_INSIGHT}}
        @segmentationFilters={{@controller.segmentationFilters}}
        {{! @glint-expect-error: Incorrect type }}
        @setFilterValues={{@controller.setFilterValues}}
        @stayDateFilter={{@controller.stayDateFilter}}
        @visibleFilters={{@controller.segmentationOrder}}
      />

      <div class="flex items-center justify-center gap-200">
        <div class="flex justify-end">
          <LinkTo
            @route="market.revenue-insight.portfolio-details-modal"
            class="prism-link"
          >
            {{t "revenue_insight.multi_pms.see_portfolio_details"}}
          </LinkTo>
        </div>

        <GeneralCurrencyPicker
          @defaultCurrencyTooltip={{t
            "revenue_insight.currency.default_in_use"
          }}
          @fallbackCurrencyCode={{@controller.riCurrency}}
          {{! @glint-expect-error: Incorrect type }}
          @onCurrencyChange={{fn (mut @controller.currency)}}
        >
          <:footer>
            <PrismButton
              @buttonType="secondary"
              @iconBefore="visibility-on"
              @label={{t "revenue_insight.view_portfolio_currencies"}}
              @onClick={{@controller.viewPortfolioCurrencies}}
              class="w-full"
            />
          </:footer>
        </GeneralCurrencyPicker>

        <GeneralFilterBarDivider />

        <PrismButton
          @buttonType="secondary"
          @icon="excel-export"
          @onClick={{@controller.exportSegmentDataMulti}}
        />
      </div>
    </div>
  </GeneralPageHeader>

  <div class="insight-page-sticky-header">
    <HotelAndMarketRevenueInsightFixedKpiHeader
      {{! @glint-expect-error: Incorrect type }}
      @allSegmentationData={{@controller.segmentations.all}}
      @changeSelectedKpi={{@controller.changeSelectedKpi}}
      @changeSelectedKpiCategory={{@controller.changeSelectedKpiCategory}}
      @kpi={{@controller.kpi}}
      {{! @glint-expect-error: Incorrect type }}
      @kpiBlocks={{@controller.kpiBlocks}}
      @kpiCategory={{@controller.kpiCategory}}
    />
  </div>

  <GeneralPageMain
    {{didUpdate
      @controller.updateDisplayedSegmentationFilters
      @controller.segmentationFilters
      @controller.stayDateFilter
      @controller.isModelLoading
    }}
    {{didUpdate
      @controller.updateShowLoadingPlaceholderState
      @controller.isModelLoading
    }}
  >
    <div class="revenue-insight content-wrapper main-content">
      {{#if @controller.showBanner}}
        <div class="mb-200">
          <section class="ri-banner">
            {{#if @controller.isModelLoading}}
              <p class="loading-pulse loading-pulse--col-6"></p>
            {{else}}
              {{#if @controller.numOfHotelsWithIssues}}
                {{#if @controller.showFullPortfolio}}
                  <p class="text-300--regular">
                    {{#each-in @controller.issueDict as |issueKey issueValue|}}
                      {{t
                        (concat "revenue_insight.warning." issueKey)
                        count=issueValue
                      }}
                      <br />
                    {{/each-in}}
                  </p>
                {{else}}
                  <p class="text-300-regular">
                    {{t
                      "revenue_insight.warning.hotels_removed"
                      count=@controller.numOfHotelsWithIssues
                    }}
                    <LinkTo @route="market.revenue-insight.issue-table">
                      <span
                        class="redirect-to-issue-modal"
                        data-event-id="market-revenue-insight-open-hotel-issue-modal"
                        data-event-value="banner-message"
                      >
                        {{t "revenue_insight.warning.review_here"}}
                      </span>
                    </LinkTo>
                    {{t "revenue_insight.warning.include_missing_hotels"}}
                  </p>
                {{/if}}
              {{/if}}
            {{/if}}
          </section>
        </div>
      {{/if}}

      {{#if @controller.isModelLoading}}
        <PrismLoadingBar @isFixed={{true}} @positionTop={{120}} />
      {{/if}}

      {{! @glint-expect-error: Incorrect type }}
      {{#if @controller.showNoGroupReportsMessage}}
        <div class="revenue-insight-message-container">
          {{t "revenue_insight.no_group_reports.title"}}
          <br />
          <br />
          <span class="description-text">
            {{t "revenue_insight.no_group_reports.message"}}
          </span>
        </div>
      {{else}}
        <section class="mb-800">
          <div class="section-title mt-0">
            <HotelAndMarketRevenueInsightSectionTitle
              {{! @glint-expect-error: Incorrect type }}
              @infoIntercomLinkLink="en/articles/5592427-choosing-your-kpi-display-in-revenue-insight"
              @infoIntercomLinkTooltipContent={{t
                "revenue_insight.multi_pms.tooltips.kpis"
              }}
              @shouldShowInfoIntercomLink={{true}}
              @titleContent={{t "revenue_insight.headers.kpis"}}
            >
              <div
                class="ri-info flex items-center justify-end"
                slot="extra-info"
              >
                <p class="ri-info__last-update-timestamp text-200--regular">
                  {{#if @controller.extractDate}}
                    {{t
                      "revenue_insight.extract_date"
                    }}{{@controller.extractDate}},
                  {{/if}}

                  {{#if @controller.updateTimestamp}}
                    {{t "general.updated"}}
                    {{! @glint-expect-error: Incorrect type }}
                    {{@controller.updateTimestamp}}
                  {{/if}}
                </p>

                {{#if @controller.riSelections.riCurrency}}
                  <PrismIcon @color="inherit" @name="currency" @size="small" />
                  <p class="text-300--regular">
                    {{@controller.riSelections.riCurrency}}
                  </p>
                {{/if}}
              </div>
            </HotelAndMarketRevenueInsightSectionTitle>
          </div>

          <div class="kpi-section-wrapper">
            <HotelAndMarketRevenueInsightKpiKpiSection
              @changeSelectedKpi={{@controller.changeSelectedKpi}}
              @changeSelectedKpiCategory={{@controller.changeSelectedKpiCategory}}
              {{! @glint-expect-error: Incorrect type }}
              @data={{get @controller.model.segmentations.all "0"}}
              @isLoading={{@controller.showLoadingPlaceholderState}}
              @kpi={{@controller.kpi}}
              @kpiBlocks={{@controller.kpiBlocks}}
              @kpiCategory={{@controller.kpiCategory}}
              @visibleKpis={{get
                @controller.visibleKpisPerBreakdown
                RI_BREAKDOWNS.ALL
              }}
            />
          </div>
        </section>

        <section class="mb-800">
          <div class="section-title">
            {{! @glint-expect-error: Incorrect type }}
            <HotelAndMarketRevenueInsightSectionTitle
              @titleContent={{t "general.hotels"}}
            >
              <div class="ri-info" slot="extra-info">
                {{#if (gt @controller.numOfHotelsWithIssues 0)}}
                  <PrismCheckbox
                    @checked={{@controller.showFullPortfolio}}
                    @onChange={{@controller.toggleFullPortfolio}}
                  >
                    {{t "revenue_insight.view_full_portfolio"}}
                  </PrismCheckbox>
                {{/if}}
              </div>
            </HotelAndMarketRevenueInsightSectionTitle>
          </div>

          <div
            class="section-content insight-grid segmentation-content--loose-bottom-margin"
          >
            <PrismCard class="segmentation-block-card block-card mr-0">
              <:content>
                <div class="hotels-block-illustration">
                  <PrismIllustration @name="320x320_skyline" @size="large" />
                  {{#unless @controller.showLoadingPlaceholderState}}
                    <div class="hotels-text-overlay">
                      <label class="text-200--regular">
                        {{t
                          "revenue_insight.hotels_in_portfolio"
                          count=@controller.hotelsCount
                        }}
                      </label>
                      <div class="text-400--bold">
                        {{@controller.hotelsCount}}
                      </div>
                    </div>
                  {{/unless}}
                </div>
              </:content>
            </PrismCard>

            <div class="hotel-block-container col-span-2">
              <HotelAndMarketRevenueInsightHotelBlockHotelBlock
                @filterLimits={{@controller.filterLimits}}
                @isLoading={{@controller.showLoadingPlaceholderState}}
                @kpi={{get
                  @controller.kpiPerBreakdown
                  RI_BREAKDOWNS.SUBSCRIPTION_ID
                }}
                @parsedSegmentationFilters={{@controller.parsedSegmentationFilters}}
                @searchValues={{get
                  @controller.searchValuesPerSegmentationId
                  RI_BREAKDOWNS.SUBSCRIPTION_ID
                }}
                {{! @glint-expect-error: Incorrect type }}
                @segmentation={{@controller.sortedSegmentationHotels}}
                @segmentationFilterClicked={{@controller.segmentationFilterClicked}}
                {{! @glint-expect-error: Incorrect type }}
                @setIsSearchActive={{@controller.setIsSearchActive}}
                {{! @glint-expect-error: Incorrect type }}
                @setSearchValue={{@controller.setSearchValue}}
                @setSortHotelsAscending={{@controller.setSortHotelsAscending}}
                @setSortHotelsBy={{@controller.setSortHotelsBy}}
                @showFullPortfolio={{@controller.showFullPortfolio}}
                @sortHotelsAscending={{@controller.sortHotelsAscending}}
                @sortHotelsBy={{@controller.sortHotelsBy}}
                @visibleKpis={{get
                  @controller.visibleKpisPerBreakdown
                  RI_BREAKDOWNS.SUBSCRIPTION_ID
                }}
              />
            </div>
          </div>
        </section>

        <section class="mb-800">
          <div class="section-title">
            {{! @glint-expect-error: Incorrect type }}
            <HotelAndMarketRevenueInsightSectionTitle
              @titleContent={{t "revenue_insight.by_impact_date"}}
            >
              <div class="stay-date-aggregation-container" slot="extra-info">
                <span class="stay-date-dropdown-label">
                  {{t "parity.view_by"}}
                </span>
                <PrismSelectInput
                  @horizontalPosition="right"
                  @onChange={{@controller.stayDateAggregationKeyChanged}}
                  @options={{@controller.stayDateAggregationOptions}}
                  @value={{@controller.selectedStayDateAggregationInDropdown}}
                />
              </div>
            </HotelAndMarketRevenueInsightSectionTitle>
          </div>

          <div class="section-content">
            <PrismCard class="revenue-insight-chart-outer-container stay-date">
              <:header as |Header|>
                <div class="title-box">
                  <div class="flex items-center justify-start gap-200">
                    {{#if @controller.showLoadingPlaceholderState}}
                      <HotelAndMarketRevenueInsightLoadingStateTitle />
                    {{else}}
                      <Header.title>
                        {{t "revenue_insight.impact_date"}}
                      </Header.title>
                      {{#if @controller.showStayDateFilterLimit}}
                        <span>
                          {{t
                            "revenue_insight.multi.max_impact_date_selection"
                            days=MAX_DRAGGABLE_DAYS
                          }}
                        </span>
                      {{/if}}
                    {{/if}}
                  </div>
                </div>
              </:header>

              <:content>
                <div class="stay-date-chart-container">
                  {{#unless @controller.showLoadingPlaceholderState}}
                    <HotelAndMarketRevenueInsightChartsChartStayDate
                      @aggregationPeriod={{@controller.stayDateAggregation}}
                      {{! @glint-expect-error: Incorrect type }}
                      @chartSelectionChanged={{@controller.chartSelectionChanged}}
                      {{! @glint-expect-error: Incorrect type }}
                      @data={{@controller.stayDateData}}
                      {{! @glint-expect-error: Incorrect type }}
                      @filterLimits={{@controller.filterLimits}}
                      @filtersActive={{@controller.filtersActive}}
                      @fromDate={{@controller.parsedFromDate}}
                      @isLoading={{@controller.isModelLoading}}
                      @isRatesEnabled={{false}}
                      @kpi={{get
                        @controller.kpiPerBreakdown
                        RI_BREAKDOWNS.IMPACT_DATE
                      }}
                      @parsedStayDateFilter={{@controller.parsedStayDateFilter}}
                      @reference={{@controller.reference}}
                      @toDate={{@controller.parsedToDate}}
                      {{! @glint-expect-error: Incorrect type }}
                      @visibleKpis={{get
                        @controller.visibleKpisPerBreakdown
                        RI_BREAKDOWNS.IMPACT_DATE
                      }}
                    />
                  {{/unless}}
                </div>
              </:content>
            </PrismCard>
          </div>
        </section>

        <section class="mb-800">
          <div class="section-title">
            {{! @glint-expect-error: Incorrect type }}
            <HotelAndMarketRevenueInsightSectionTitle
              @titleContent={{t "revenue_insight.by_pickup_and_pace"}}
            />
          </div>

          <div
            class="section-content insight-grid segmentation-content--loose-bottom-margin pace"
          >
            <HotelAndMarketRevenueInsightSegmentationSegmentationBlock
              @filterLimits={{@controller.filterLimits}}
              @isLoading={{@controller.showLoadingPlaceholderState}}
              @kpi={{get @controller.kpiPerBreakdown RI_BREAKDOWNS.PICKUP}}
              @parsedSegmentationFilters={{@controller.parsedSegmentationFilters}}
              @searchValues={{get
                @controller.searchValuesPerSegmentationId
                RI_BREAKDOWNS.PICKUP
              }}
              {{! @glint-expect-error Incorrect type }}
              @segmentation={{@controller.sortedSegmentationPickup}}
              @segmentationFilterClicked={{@controller.segmentationFilterClicked}}
              {{! @glint-expect-error Incorrect type }}
              @setIsSearchActive={{@controller.setIsSearchActive}}
              {{! @glint-expect-error Incorrect type }}
              @setSearchValue={{@controller.setSearchValue}}
              @visibleKpis={{get
                @controller.visibleKpisPerBreakdown
                RI_BREAKDOWNS.PICKUP
              }}
            />

            <HotelAndMarketRevenueInsightChartContainer
              @kpi={{get @controller.kpiPerBreakdown RI_BREAKDOWNS.PACE}}
              @showLoadingPlaceholderState={{@controller.showLoadingPlaceholderState}}
              class="revenue-insight-chart-outer-container pace revenue-chart col-span-2"
            >
              <HotelAndMarketRevenueInsightChartsChartPace
                {{! @glint-expect-error: Incorrect type }}
                @allSegmentation={{@controller.segmentations.all}}
                {{! @glint-expect-error: Incorrect type }}
                @chartSelectionChanged={{@controller.chartSelectionChanged}}
                {{! @glint-expect-error: Incorrect type }}
                @data={{@controller.segmentations.pace}}
                {{! @glint-expect-error: Incorrect type }}
                @filterLimits={{@controller.filterLimits}}
                @filtersActive={{@controller.filtersActive}}
                @fromDate={{@controller.fromDate}}
                @isLoading={{@controller.showLoadingPlaceholderState}}
                {{! @glint-expect-error: Incorrect type }}
                @kpi={{get @controller.kpiPerBreakdown RI_BREAKDOWNS.PACE}}
                @kpiCategory={{@controller.kpiCategory}}
                @stayDateFilter={{@controller.stayDateFilter}}
                @toDate={{@controller.toDate}}
                {{! @glint-expect-error: Incorrect type }}
                @visibleKpis={{get
                  @controller.visibleKpisPerBreakdown
                  RI_BREAKDOWNS.PACE
                }}
              />
            </HotelAndMarketRevenueInsightChartContainer>
          </div>
        </section>
      {{/if}}

      <section>
        <div class="section-title flex items-center justify-between">
          <HotelAndMarketRevenueInsightSectionTitle
            {{! @glint-expect-error: Incorrect type }}
            @infoIntercomLinkLink="en/articles/2459363-revenue-insight-breakdown-cards-and-how-to-use-them"
            @infoIntercomLinkTooltipContent={{t
              "revenue_insight.multi_pms.tooltips.breakdowns"
            }}
            @shouldShowInfoIntercomLink={{true}}
            @titleContent={{t "revenue_insight.headers.segments"}}
          />

          <PrismButton
            @buttonType="secondary"
            @iconBefore="sliders"
            @label={{t "revenue_insight.menu.preferences"}}
            @onClick={{@controller.openSelectionAndSortModal}}
            class="ic-product-tour-ri-multi--breakdown-settings"
          />
        </div>

        <div
          class="section-content insight-grid ic-product-tour-ri-multi--breakdowns"
        >
          {{#each @controller.visibleSegmentationIds as |segmentationId index|}}
            <HotelAndMarketRevenueInsightSegmentationSegmentationBlock
              @extra={{hash
                hasHotelsThatMapByRateCode=@controller.hasHotelsThatMapByRateCode
              }}
              @filterLimits={{@controller.filterLimits}}
              @isLoading={{@controller.showLoadingPlaceholderState}}
              @kpi={{get @controller.kpiPerBreakdown segmentationId}}
              @parsedSegmentationFilters={{@controller.parsedSegmentationFilters}}
              {{! @glint-expect-error Incorrect type }}
              @searchValues={{get
                @controller.searchValuesPerSegmentationId
                segmentationId
              }}
              {{! @glint-expect-error Incorrect type }}
              @segmentation={{get
                @controller.sortedVisibleSegmentations
                segmentationId
              }}
              @segmentationBlockIdx={{index}}
              @segmentationFilterClicked={{@controller.segmentationFilterClicked}}
              {{! @glint-expect-error Incorrect type }}
              @setIsSearchActive={{@controller.setIsSearchActive}}
              {{! @glint-expect-error Incorrect type }}
              @setSearchValue={{@controller.setSearchValue}}
              @visibleKpis={{get
                @controller.visibleKpisPerBreakdown
                segmentationId
              }}
            />
          {{/each}}
        </div>
      </section>
    </div>
  </GeneralPageMain>

  {{#if @controller.isShowingSelectionAndSortModal}}
    {{!
      Modal should have the height of the content
      The content does not expand or change in a way that changes the height
      This would give too many complications with the width calculation and would result in a permanent scrollbar
    }}
    <PrismModal
      @onClose={{@controller.closeSelectionAndSortModal}}
      @title={{t "revenue_insight.breakdown_settings.title"}}
    >
      <HotelAndMarketRevenueInsightSortAndReorderBlocks
        @closeModal={{@controller.closeSelectionAndSortModal}}
        @doRefresh={{@controller.refreshRoute}}
        @segmentationBlocksOrder={{@controller.segmentationBlocksOrder}}
      />
    </PrismModal>
  {{/if}}

  {{outlet}}
</template> satisfies TOC<MarketRevenueInsightSignature>;
